import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { AadhaarNumberField } from "../../../components/common/form/AadhaarNumberField";
import { MobileNumberField } from "../../../components/common/form/MobileNumberField";
import UpperCaseTextField from "../../../modules/common/shared/components/UpperCaseTextField";
import { useNotify } from "../../../context/NotificationContext";
import { useCreateStaff } from "../hooks/useCreateStaff";
import { Staff, StaffType, StaffTypeLabel } from "../types";
import { contentCardSx } from "../../../components/common/surfaceStyles";

const emptyForm: Staff = {
  name: "",
  aadhaarNumber: "",
  gender: "Male",
  active: true,
  email: "",
  phone: "",
  role: StaffType.LIBRARIAN,
};

/**
 * Add support staff (Staff → Add, "Staff" selected). Same layout as the faculty form. After a save
 * the form is cleared for the next entry; on failure the input is kept and the reason shown.
 */
export default function StaffAddForm() {
  const notify = useNotify();
  const { createStaff, saving } = useCreateStaff();
  const [form, setForm] = useState<Staff>(emptyForm);
  // Bumped after a save so the fields remount clean (phone/Aadhaar forget they were touched).
  const [formKey, setFormKey] = useState(0);

  const setField = (field: keyof Staff, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const failure = await createStaff(form);
    if (failure) {
      notify(failure, "error");
      return;
    }
    notify("Staff created successfully.", "success");
    setForm(emptyForm);
    setFormKey((key) => key + 1);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ ...contentCardSx, display: "flex", flexDirection: "column", gap: 2, ml: { xs: 0, md: "100px" }, mt: 2, maxWidth: 800 }}
    >
      <Box>
        <Typography variant="h5" fontWeight={600}>
          Add Staff
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Support staff (librarian, security, janitor). No login is created.
        </Typography>
      </Box>

      <Box key={formKey} display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
        <UpperCaseTextField value={form.name} setValue={(value) => setField("name", value)} label="Name" required={true} />
        <FormControl fullWidth size="small">
          <InputLabel id="staff-gender">Gender</InputLabel>
          <Select labelId="staff-gender" label="Gender" value={form.gender} onChange={(event) => setField("gender", event.target.value)}>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </Select>
        </FormControl>
        <MobileNumberField
          label="Phone"
          value={form.phone || null}
          onChange={(phone) => setField("phone", phone ?? "")}
          required={true}
          fullWidth
        />
        <TextField
          size="small"
          fullWidth
          label="Email"
          type="email"
          value={form.email}
          onChange={(event) => setField("email", event.target.value)}
        />
        <AadhaarNumberField
          label="Aadhaar No"
          value={form.aadhaarNumber || null}
          onChange={(aadhaar) => setField("aadhaarNumber", aadhaar ?? "")}
          required={true}
          width="100%"
        />
        <TextField
          select
          size="small"
          fullWidth
          required
          label="Role"
          value={form.role}
          onChange={(event) => setField("role", event.target.value)}
        >
          {Object.entries(StaffTypeLabel).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box display="flex" gap={1}>
        <Button variant="contained" type="submit" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Add"}
        </Button>
      </Box>
    </Box>
  );
}
